import React, { useState, useCallback } from 'react';
import { Button, makeStyles, Typography } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { WallSide, EntrancePosition } from '../context';
import { useTableMapWizard } from '../context';
import MapEditorCanvas, { ZOOM_MIN, ZOOM_MAX, ZOOM_STEP } from '../components/MapEditorCanvas';

const MAGENTA = theme.palette.primary.main;
const MAGENTA_LIGHT = 'rgba(153, 86, 126, 0.08)';
const MAGENTA_BORDER = 'rgba(153, 86, 126, 0.3)';

const useStyles = makeStyles({
    root: {
        padding: '32px 40px',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
    },
    heading: {
        fontSize: 16,
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: 4,
    },
    infoBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        borderRadius: 6,
        backgroundColor: MAGENTA_LIGHT,
        border: `1px solid ${MAGENTA_BORDER}`,
    },
    infoBannerIcon: {
        color: MAGENTA,
        fontSize: 20,
        flexShrink: 0,
    },
    infoBannerText: {
        fontSize: 13,
        color: MAGENTA,
        fontWeight: 500,
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
    },
    toolbarLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    toolbarRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    gridLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
    },
    gridToggleButton: {
        minWidth: 48,
        height: 32,
        padding: '0 12px',
        fontSize: 12,
        fontWeight: 600,
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey['300']}`,
        cursor: 'pointer',
        letterSpacing: 0.5,
    },
    gridToggleOn: {
        backgroundColor: MAGENTA,
        color: 'white',
        borderColor: MAGENTA,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    gridToggleOff: {
        backgroundColor: 'white',
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
    },
    markEntranceButton: {
        height: 32,
        padding: '0 14px',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.5,
        textTransform: 'uppercase' as const,
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    markEntranceActive: {
        backgroundColor: MAGENTA,
        color: 'white',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    markEntranceInactive: {
        backgroundColor: 'white',
        color: MAGENTA,
        border: `1px solid ${MAGENTA}`,
        '&:hover': {
            backgroundColor: MAGENTA_LIGHT,
        },
    },
    removeEntranceButton: {
        background: 'none',
        border: 'none',
        padding: '0 4px',
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 600,
        color: MAGENTA,
        letterSpacing: 0.3,
        textDecoration: 'underline',
        '&:hover': {
            color: theme.palette.primary.dark,
        },
    },
    zoomControls: {
        display: 'flex',
        alignItems: 'center',
        border: `1px solid ${theme.palette.grey['300']}`,
        borderRadius: 4,
        overflow: 'hidden',
    },
    zoomButton: {
        width: 32,
        height: 32,
        border: 'none',
        background: 'white',
        cursor: 'pointer',
        fontSize: 18,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
            backgroundColor: theme.palette.grey['300'],
        },
        '&:disabled': {
            color: theme.palette.grey['400'],
            cursor: 'not-allowed',
        },
    },
    zoomLabel: {
        minWidth: 48,
        textAlign: 'center' as const,
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        borderLeft: `1px solid ${theme.palette.grey['300']}`,
        borderRight: `1px solid ${theme.palette.grey['300']}`,
        lineHeight: '32px',
        userSelect: 'none' as const,
    },
    body: {
        display: 'flex',
        gap: 0,
        alignItems: 'flex-start',
    },
    sideSpacer: {
        width: 272,
        flexShrink: 0,
    },
    canvasColumn: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12,
    },
    canvasWrapper: {
        width: '100%',
        height: 440,
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.grey['300']}`,
    },
    hintText: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        textAlign: 'center' as const,
        minHeight: 18,
        paddingLeft: 272,
        paddingRight: 272,
    },
});

export default function MapConfigurationStep() {
    const classes = useStyles();
    const { draft, dispatch } = useTableMapWizard();

    const [zoom, setZoom] = useState(1.0);
    const [isMarkingEntrance, setIsMarkingEntrance] = useState(false);

    function handleGridToggle() {
        dispatch({ type: 'SET_GRID_ENABLED', payload: !draft.gridEnabled });
    }

    function handleMarkEntranceToggle() {
        setIsMarkingEntrance((prev) => !prev);
    }

    function handleRemoveEntrance() {
        dispatch({ type: 'SET_ENTRANCE', payload: null });
        setIsMarkingEntrance(false);
    }

    function handleZoomIn() {
        setZoom((prev) => Math.min(ZOOM_MAX, parseFloat((prev + ZOOM_STEP).toFixed(2))));
    }

    function handleZoomOut() {
        setZoom((prev) => Math.max(ZOOM_MIN, parseFloat((prev - ZOOM_STEP).toFixed(2))));
    }

    const handleSelectWall = useCallback(
        (wall: WallSide, position: number) => {
            const entrance: EntrancePosition = { wall, position };
            dispatch({ type: 'SET_ENTRANCE', payload: entrance });
            setIsMarkingEntrance(false);
        },
        [dispatch]
    );

    const handleMoveEntrance = useCallback(
        (position: number) => {
            if (!draft.entrance) return;
            dispatch({
                type: 'SET_ENTRANCE',
                payload: { wall: draft.entrance.wall, position },
            });
        },
        [draft.entrance, dispatch]
    );

    const zoomPercent = Math.round(zoom * 100);

    const entranceExists = draft.entrance !== null;

    return (
        <div className={classes.root}>
            <Typography className={classes.heading}>
                {i18n.t('tableMaps.wizard.mapConfiguration')}
            </Typography>

            {isMarkingEntrance && (
                <div className={classes.infoBanner}>
                    <InfoOutlinedIcon className={classes.infoBannerIcon} />
                    <Typography className={classes.infoBannerText}>
                        {i18n.t('tableMaps.wizard.selectWallHint')}
                    </Typography>
                </div>
            )}

            <div className={classes.body}>
                <div className={classes.sideSpacer} />

                <div className={classes.canvasColumn}>
                    <div className={classes.toolbar}>
                        <div className={classes.toolbarLeft}>
                            <Typography className={classes.gridLabel}>
                                {i18n.t('tableMaps.wizard.grid')}
                            </Typography>
                            <button
                                className={`${classes.gridToggleButton} ${
                                    draft.gridEnabled ? classes.gridToggleOn : classes.gridToggleOff
                                }`}
                                onClick={handleGridToggle}
                                type="button"
                            >
                                {draft.gridEnabled
                                    ? i18n.t('tableMaps.wizard.on')
                                    : i18n.t('tableMaps.wizard.off')}
                            </button>
                        </div>

                        <div className={classes.toolbarRight}>
                            {entranceExists && (
                                <button
                                    className={classes.removeEntranceButton}
                                    onClick={handleRemoveEntrance}
                                    type="button"
                                >
                                    {i18n.t('tableMaps.wizard.removeEntrance')}
                                </button>
                            )}

                            <Button
                                variant={isMarkingEntrance ? 'contained' : 'outlined'}
                                className={`${classes.markEntranceButton} ${
                                    isMarkingEntrance
                                        ? classes.markEntranceActive
                                        : classes.markEntranceInactive
                                }`}
                                onClick={handleMarkEntranceToggle}
                                startIcon={<MeetingRoomIcon style={{ fontSize: 16 }} />}
                                disabled={entranceExists}
                            >
                                {i18n.t('tableMaps.wizard.markEntrance')}
                            </Button>

                            <div className={classes.zoomControls}>
                                <button
                                    className={classes.zoomButton}
                                    onClick={handleZoomOut}
                                    disabled={zoom <= ZOOM_MIN}
                                    type="button"
                                    aria-label="Zoom out"
                                >
                                    −
                                </button>
                                <span className={classes.zoomLabel}>{zoomPercent}%</span>
                                <button
                                    className={classes.zoomButton}
                                    onClick={handleZoomIn}
                                    disabled={zoom >= ZOOM_MAX}
                                    type="button"
                                    aria-label="Zoom in"
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={classes.canvasWrapper}>
                        <MapEditorCanvas
                            gridEnabled={draft.gridEnabled}
                            zoom={zoom}
                            entrance={draft.entrance}
                            isMarkingEntrance={isMarkingEntrance}
                            onSelectWall={handleSelectWall}
                            onMoveEntrance={handleMoveEntrance}
                            allowEntranceDrag
                        />
                    </div>
                </div>

                <div className={classes.sideSpacer} />
            </div>

            <Typography className={classes.hintText}>
                {entranceExists ? i18n.t('tableMaps.wizard.dragEntranceHint') : ''}
            </Typography>
        </div>
    );
}
